import {
    computed,
    DestroyRef,
    Directive,
    ElementRef,
    HostBinding,
    HostListener,
    inject,
    input,
    signal,
} from '@angular/core';
import {StepFocusService} from './step-focus.service';
import {StepState} from './step-state.model';

@Directive({
    selector: '[appStepAnchor]',
    standalone: true,
    host: {
        style: 'scroll-margin-top: var(--step-sticky-offset, 1rem); transition: opacity 0.25s ease',
    },
})
export class StepAnchorDirective {
    private readonly stepFocus = inject(StepFocusService);

    readonly stepId = input.required<string>({alias: 'appStepAnchor'});
    readonly state = input.required<StepState>();

    readonly current = computed(() => this.state().place === 'current');

    readonly element = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;

    private readonly hovered = signal(false);

    private jumpsOnClick = false;

    private readonly opacity = computed(() => {
        if (this.current()) {
            return null;
        }
        return this.hovered() ? 0.65 : 0.35;
    });

    constructor() {
        inject(DestroyRef).onDestroy(this.stepFocus.register(this));
    }

    @HostBinding('style.opacity')
    protected get opacityStyle(): number | null {
        return this.opacity();
    }

    @HostListener('focusin')
    protected enterStep(): void {
        this.stepFocus.enter(this.stepId());
    }

    @HostListener('pointerdown')
    protected pressStep(): void {
        this.jumpsOnClick = !this.current();
        this.stepFocus.enter(this.stepId());
    }

    @HostListener('click')
    protected jumpToStep(): void {
        if (this.jumpsOnClick) {
            this.jumpsOnClick = false;
            this.stepFocus.select(this.stepId());
        }
    }

    @HostListener('mouseenter')
    protected startHover(): void {
        this.hovered.set(true);
    }

    @HostListener('mouseleave')
    protected endHover(): void {
        this.hovered.set(false);
    }
}
